import { Bell } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Portal notification bell (GAP-022). Client-side users see their OWN notifications
 * (new invoice → client_admin; subscription changes → client_admin + tagged
 * client_users). Reuses the user-scoped notifications API.
 */
export function PortalNotificationBell(): JSX.Element {
  const utils = trpc.useUtils();
  const unread = trpc.notifications.unreadCount.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const list = trpc.notifications.list.useQuery({ limit: 8 });
  const markAll = trpc.notifications.markAllRead.useMutation({
    onSuccess: () => {
      void utils.notifications.unreadCount.invalidate();
      void utils.notifications.list.invalidate();
    },
  });
  const count = unread.data?.count ?? 0;
  const items =
    (list.data as
      | { id: string; title: string; body: string; isRead: boolean }[]
      | undefined) ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          title="Notifications"
          className="relative"
        >
          <Bell className="size-5" />
          {count > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          {count > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              className="text-xs text-muted-foreground hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>
        <Separator />
        {items.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">
            You’re all caught up.
          </p>
        ) : (
          <ul className="max-h-96 overflow-auto py-1">
            {items.map((n) => (
              <li
                key={n.id}
                className={cn("px-3 py-2.5", !n.isRead && "bg-muted/40")}
              >
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-sm text-muted-foreground">{n.body}</p>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
